/* LMRBLayoutRenderer.js */

"use strict";
/* jshint expr: true */

const QuadrantComponent = require("./quadrant/QuadrantComponent");
const QUADRANTS = ["left", "middle", "right", "bottom"];
const _ = require("lodash");

class LMRBLayoutRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("lmrb-layout"); //TODO Setup the root Element
        this._rootElem.component = this._model;
        this._topContainer = document.createElement("lmrb-top-container");
        this._bottomContainer = document.createElement("lmrb-bottom-container");

        this._quadrants = {};
        QUADRANTS.forEach((name) => {
            this._quadrants[name] = new QuadrantComponent({
                name: name,
                state: this._model._state[name],
            });
        });

        this._registerListeners();
        //this.render();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */

    /********************************************/
    shutdown() {
        let prom = new Promise((resolve, reject) => {
            this._unregisterListeners();
            let quadPromises = [];
            QUADRANTS.forEach((name) => {
                quadPromises.push(this._quadrants[name].shutdown());
            });
            this._rootElem.component = null;
            this._topContainer.remove();
            this._topContainer = null;
            this._bottomContainer.remove();
            this._bottomContainer = null;
            this._rootElem.remove();
            this._rootElem = null;
            Promise.all(quadPromises).then(resolve, reject);
        });
        return prom;
    }

    _registerListeners() {
        this._resizeHandler = this._resizeHandler.bind(this);
        this._quadrantCreateLMRBComponentHandler =
            this._quadrantCreateLMRBComponentHandler.bind(this);
        this._model.on("Shutdown", () => {
            this.shutdown();
        });

        //quadrant menu state change handler/listeners
        QUADRANTS.forEach((name) => {
            this._model._state[name].on("stateChanged", () => {
                this._quadrantStateChangeHandler(name, this._model._state[name]);
            });
            this._quadrants[name].on("activatePart", this._quadrantCreateLMRBComponentHandler);
        });

        this._setResizeHandler(this._model._resizeDebounce);
    }

    _unregisterListeners() {
        window.removeEventListener("resize", this._debouncedResizeHandler);
    }

    _setResizeHandler(time) {
        this._debouncedResizeHandler = _.debounce(this._resizeHandler, time, {trailing: true});
        window.addEventListener("resize", this._debouncedResizeHandler, false);
    }

    _resizeHandler() {
        this._model._state.notifyWindowSizeChanged();
    }

    _quadrantCreateLMRBComponentHandler(quadName, LMRBComponentType) {
        this._model.activateComponent(quadName, LMRBComponentType);
    }

    _quadrantStateChangeHandler(quadName, state) {
        if (state.disabled && this._quadrants[quadName]) {
            this._quadrants[quadName].shutdown();
            this._quadrants[quadName] = null;
        }
        if (!state.disabled && !this._quadrants[quadName]) {
            this._quadrants[quadName] = new QuadrantComponent({
                name: quadName,
                state: this._model._state[quadName],
            });
            if (quadName === "left") {
                this._topContainer.insertBefore(
                    this._quadrants.left.renderer.rootElement,
                    this._quadrants.middle.renderer.rootElement
                );
            }
            if (quadName === "right") {
                this._topContainer.appendChild(this._quadrants.right.renderer.rootElement);
            }
            this._quadrants[quadName].renderer.render();
        }
        state.pinned
            ? this._setQuadrantPinnedClass(quadName)
            : this._removeQuadrantPinnedClass(quadName);
        state.optionsOpened
            ? this._setOpenOptionsClass(quadName)
            : this._removeOpenOptionsClass(quadName);
        state.menuVisible
            ? this._setHoverPartsListClass(quadName)
            : this._removeHoverPartsListClass(quadName);
    }

    _setQuadrantPinnedClass(name) {
        this._rootElem.classList.add("pinned-" + name);
    }

    _removeQuadrantPinnedClass(name) {
        this._rootElem.classList.remove("pinned-" + name);
    }

    _setOpenOptionsClass(name) {
        this._rootElem.classList.add("options-open-" + name);
    }

    _removeOpenOptionsClass(name) {
        this._rootElem.classList.remove("options-open-" + name);
    }

    _setHoverPartsListClass(name) {
        this._rootElem.classList.add("options-hovered-" + name);
    }

    _removeHoverPartsListClass(name) {
        this._rootElem.classList.remove("options-hovered-" + name);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */

    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     */
    render() {
        return new Promise((resolve, reject) => {
            if (!this.rootElement.firstChild) {
                this.rootElement.appendChild(this._topContainer);
                this.rootElement.appendChild(this._bottomContainer);

                QUADRANTS.forEach((name) => {
                    if (!this.model._state[name].disabled) {
                        if (name === "bottom") {
                            this._bottomContainer.appendChild(
                                this._quadrants[name].renderer.rootElement
                            );
                        } else {
                            this._topContainer.appendChild(
                                this._quadrants[name].renderer.rootElement
                            );
                        }
                    }
                });
            }
            let renderPromises = [];
            QUADRANTS.forEach((name) => {
                if (!this.model._state[name].disabled) {
                    this._quadrantStateChangeHandler(name, this._model._state[name]);
                    renderPromises.push(this._quadrants[name].renderer.render());
                }
            });
            Promise.all(renderPromises).then(resolve, reject);
        });
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/
    //TODO - Nobody should be using methods on a Renderer directly so make all these methods private
    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = LMRBLayoutRenderer;
